import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { PaymentRouter } from "@/config/paymentRouter";
import { DespiaIap } from "@/lib/despiaIap";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { PlanTier } from "@/lib/plans";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
  plan: PlanTier;
  subscribed: boolean;
  subscriptionEnd: string | null;
  isTrial: boolean;
  trialEnd: string | null;
  refreshSubscription: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
  plan: "free",
  subscribed: false,
  subscriptionEnd: null,
  isTrial: false,
  trialEnd: null,
  refreshSubscription: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState<PlanTier>("free");
  const [subscribed, setSubscribed] = useState(false);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [isTrial, setIsTrial] = useState(false);
  const [trialEnd, setTrialEnd] = useState<string | null>(null);

  const refreshSubscription = useCallback(async () => {
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    if (!currentSession) return;
    try {
      // Admins always have full access — treat them as Business plan so every
      // plan-gated feature unlocks site-wide without needing per-component
      // admin checks.
      try {
        const { data: isAdmin } = await supabase.rpc("has_role", {
          _user_id: currentSession.user.id,
          _role: "admin" as any,
        });
        if (isAdmin) {
          setPlan("business");
          setSubscribed(true);
          setSubscriptionEnd(null);
          setIsTrial(false);
          setTrialEnd(null);
          return;
        }
      } catch {
        // Fall through to normal subscription check on any RPC error
      }
      // Native store apps: link the RevenueCat customer to this account so
      // purchases made in the App Store / Google Play follow the user everywhere.
      if (PaymentRouter.isMobileNative) {
        void DespiaIap.identify(currentSession.user.id);
      }
      const { data, error } = await supabase.functions.invoke("check-subscription");
      if (error) {
        // Network blips / transient edge function errors shouldn't spam the console.
        // Subscription state safely falls back to current values.
        const msg = (error as any)?.message || String(error);
        if (!/Failed to (fetch|send)/i.test(msg)) {
          console.warn("Subscription check error:", msg);
        }
        return;
      }
      setPlan((data?.plan as PlanTier) || "free");
      setSubscribed(!!data?.subscribed);
      setSubscriptionEnd(data?.subscription_end || null);
      setIsTrial(!!data?.is_trial);
      setTrialEnd(data?.trial_end || null);
    } catch (err) {
      const msg = (err as any)?.message || String(err);
      if (!/Failed to (fetch|send)/i.test(msg)) {
        console.warn("Subscription check failed:", msg);
      }
    }
  }, []);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        if (_event === "TOKEN_REFRESHED" && !session) {
          // Token refresh failed — clear stale state
          console.warn("Token refresh failed, signing out");
          supabase.auth.signOut();
          return;
        }

        if (session?.user) {
          setTimeout(() => refreshSubscription(), 0);
          if (_event === "SIGNED_IN") {
            supabase.from("activity_logs" as any).insert({
              user_id: session.user.id,
              action: "login",
              entity_type: "auth",
              details: { method: session.user.app_metadata?.provider || "email" },
              user_agent: navigator.userAgent,
            } as any).then(() => {});
          }
        } else {
          setPlan("free");
          setSubscribed(false);
          setSubscriptionEnd(null);
          setIsTrial(false);
          setTrialEnd(null);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) {
        refreshSubscription();
      }
    });

    return () => subscription.unsubscribe();
  }, [refreshSubscription]);

  // Periodic refresh every 10 minutes (reduced from 60s to cut backend load)
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(refreshSubscription, 10 * 60_000);
    return () => clearInterval(interval);
  }, [user, refreshSubscription]);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut, plan, subscribed, subscriptionEnd, isTrial, trialEnd, refreshSubscription }}>
      {children}
    </AuthContext.Provider>
  );
};
